import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import Loading from "@/components/Loading";
import { ChevronLeft, Calendar, MessageSquare, Edit, ArrowLeft, MessageCircle, Mail, Phone, Dumbbell, Activity, User, Ruler, Weight, HeartPulse, Award } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import ClientProfileCard from "@/components/clients/ClientProfileCard";
import ClientPackagesTab from "@/components/clients/ClientPackagesTab";
import ClientCoursesTab from "@/components/clients/ClientCoursesTab";
import ClientWorkoutsTab from "@/components/clients/ClientWorkoutsTab";
import ClientSessionsTab from "@/components/clients/session-tabs/ClientSessionsTab";
import ClientPaymentsTab from "@/components/clients/ClientPaymentsTab";
import ClientInfoTab from "@/components/clients/ClientInfoTab";
import ClientFormDialog from "@/components/clients/ClientFormDialog";
import PackageDialog from "@/components/clients/PackageDialog";
import CourseDialog from "@/components/clients/CourseDialog";
import WorkoutDialog from "@/components/clients/WorkoutDialog";
import PaymentDialog from "@/components/clients/PaymentDialog";
import WorkoutDetailsDialog from "@/components/workouts/WorkoutDetailsDialog";
import { useClientDetail } from "@/hooks/use-client-detail";
import { useClientInfo } from "@/hooks/use-client-info";
import { useIsMobile } from "@/hooks/use-mobile";
const ClientDetail = () => {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const location = useLocation();
  const navigate = useNavigate();
  const clientId = location.state?.clientId;
  const {
    isClientView,
    isAdmin
  } = useClientInfo();
  const isMobile = useIsMobile();
  const {
    client,
    isLoading,
    clientPackages,
    availablePackages,
    selectedPackageId,
    setSelectedPackageId,
    isPackageDialogOpen,
    setIsPackageDialogOpen,
    clientCourses,
    availableCourses,
    selectedCourseId,
    setSelectedCourseId,
    isCourseDialogOpen,
    setIsCourseDialogOpen,
    clientWorkouts,
    availableWorkouts,
    selectedWorkoutId,
    setSelectedWorkoutId,
    isWorkoutDialogOpen,
    setIsWorkoutDialogOpen,
    isWorkoutDetailsDialogOpen,
    setIsWorkoutDetailsDialogOpen,
    selectedWorkout,
    payments,
    isPaymentDialogOpen,
    setIsPaymentDialogOpen,
    newPayment,
    isEditDialogOpen,
    setIsEditDialogOpen,
    isSaving,
    formData,
    handleInputChange,
    handleSelectChange,
    handleImageUpload,
    handleSaveClient,
    handleAssignPackage,
    handleEnrollCourse,
    handleAssignWorkout,
    handleDeletePackage,
    handleDeleteCourse,
    handleDeleteWorkout,
    handleViewWorkout,
    handleAddPayment
  } = useClientDetail(slug, clientId);
  const handleNavigateToCalendar = () => {
    navigate('/admin/calendar', {
      state: {
        clientId: client?.id
      }
    });
  };
  const handleGoBack = () => {
    navigate(-1);
  };
  if (isLoading) {
    return <Loading />;
  }
  if (!client) {
    return <div>
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold mb-4">Cliente non trovato</h1>
          <p className="text-muted-foreground">Il cliente richiesto non esiste o non è accessibile.</p>
        </div>
      </div>;
  }
  const clientFullName = `${client.first_name} ${client.last_name}`;

  // Calcoliamo il BMI per utilizzarlo nel banner
  const calculateBMI = () => {
    if (client.height && client.weight) {
      const heightInMeters = client.height / 100;
      const bmi = client.weight / (heightInMeters * heightInMeters);
      return bmi.toFixed(1);
    }
    return "N/D";
  };

  // Formattazione numero telefono per WhatsApp
  const formatPhoneForWhatsApp = (phone: string) => {
    if (!phone) return "";
    const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
    if (cleanPhone.startsWith('0')) {
      return `+39${cleanPhone}`;
    }
    if (!cleanPhone.startsWith('+')) {
      return `+39${cleanPhone}`;
    }
    return cleanPhone;
  };

  // Gestione sicura dei valori nulli per l'avatar
  const firstName = client.first_name || '';
  const lastName = client.last_name || '';
  const initials = `${firstName.charAt(0) || '?'}${lastName.charAt(0) || '?'}`;

  // Genera un colore univoco in base all'id del cliente
  const getClientAvatarBorderColor = () => {
    // Se il cliente ha un color_code personalizzato, lo utilizziamo
    if (client.color_code) return client.color_code;

    // Altrimenti generiamo un colore basato sull'id
    if (!client.id) return '#9b87f5'; // Colore primario di default

    // Utilizziamo l'id del cliente per generare un colore coerente
    const hash = client.id.split('').reduce((acc, char) => {
      return char.charCodeAt(0) + ((acc << 5) - acc);
    }, 0);

    // Convertiamo l'hash in un colore HSL con saturazione e luminosità fisse
    const h = Math.abs(hash % 360);
    return `hsl(${h}, 80%, 65%)`;
  };
  const avatarBorderColor = getClientAvatarBorderColor();
  return <div className="animate-fade-in space-y-6">
      {/* Banner integrato con informazioni del cliente */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-primary/10 to-primary/5 p-6 shadow-sm mb-6">
        <div className="absolute inset-0 bg-white/40 backdrop-blur-sm dark:bg-black/20"></div>
        
        <div className="relative">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={handleGoBack} className="rounded-full hover:scale-110 transition-transform hover:bg-white/80 hover:text-primary">
                <ArrowLeft size={20} />
              </Button>
              
              <div>
                <h1 className="text-2xl font-bold">{clientFullName}</h1>
                {!isClientView && <p className="text-muted-foreground mt-1">
                    Gestisci i dettagli e monitora le attività
                  </p>}
              </div>
            </div>
            
            <div className="flex gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline" size="icon" className="rounded-full shadow-sm bg-white/80 hover:scale-105 transition-all">
                      <MessageSquare size={18} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Invia messaggio</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              
              <Button variant="outline" className="gap-2 shadow-sm rounded-full bg-white/80 px-4 hover:scale-105 transition-all" onClick={() => setIsEditDialogOpen(true)}>
                <Edit size={16} />
                <span>Modifica</span>
              </Button>
              
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline" size="icon" className="rounded-full shadow-sm bg-white/80 hover:scale-105 transition-all" onClick={handleNavigateToCalendar}>
                      <Calendar size={18} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Vai al calendario</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
          
          {/* Contenuto del ClientProfileCard integrato nel banner */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 items-center pt-2">
            {/* Colonna Avatar e Informazioni Base */}
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4">
              <div className={`ring ring-[${avatarBorderColor}] rounded-full p-1 bg-white animate-scale-in transition-all duration-500`} style={{
              borderColor: avatarBorderColor
            }}>
                <Avatar className={`h-20 w-20 border-4 shadow-md mb-2 sm:mb-0 hover:scale-105 transition-transform duration-300`} style={{
                borderColor: avatarBorderColor
              }}>
                  <AvatarImage src={client.avatar_url || undefined} />
                  <AvatarFallback className="text-xl bg-primary/20 text-primary font-bold">
                    {initials}
                  </AvatarFallback>
                </Avatar>
              </div>
              
              <div className="flex flex-col items-center sm:items-start">
                <div className="flex items-center gap-2 mb-1">
                  <Badge className="bg-green-500 hover:bg-green-600 animate-pulse">Attivo</Badge>
                </div>
                
                <div className="flex flex-wrap gap-2 mt-2">
                  {client.telefono && <div className="flex gap-2">
                      <a href={`https://wa.me/${formatPhoneForWhatsApp(client.telefono)}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-xs bg-green-500 text-white px-3 py-1.5 rounded-full hover:bg-green-600 transition-colors">
                        <MessageCircle size={14} />
                        <span>WhatsApp</span>
                      </a>
                      
                      
                    </div>}
                </div>
              </div>
            </div>
            
            {/* Colonna Contatti */}
            <div className="bg-white/80 p-3 rounded-lg shadow-sm hover:shadow transition-shadow duration-300">
              <h3 className="font-medium text-sm mb-2 flex items-center gap-1.5">
                <MessageSquare size={14} className="text-primary" />
                Contatti
              </h3>
              
              <div className="space-y-2 text-sm">
                {client.email && <div className="flex items-center justify-between bg-white p-2 rounded-md hover:bg-gray-50 hover:shadow-sm transition-all">
                    <a href={`mailto:${client.email}`} className="flex items-center gap-2 hover:text-primary transition-colors w-full">
                      <Mail size={16} className="text-blue-500" />
                      <span className="truncate max-w-[150px]" title={client.email}>
                        {client.email}
                      </span>
                    </a>
                  </div>}
                
                {client.telefono && <div className="flex items-center justify-between bg-white p-2 rounded-md hover:bg-gray-50 hover:shadow-sm transition-all">
                    <a href={`tel:${client.telefono}`} className="flex items-center gap-2 hover:text-primary transition-colors w-full">
                      <Phone size={16} className="text-blue-500" />
                      <span className="truncate max-w-[150px]">
                        {client.telefono}
                      </span>
                    </a>
                  </div>}
                
                {!client.email && !client.telefono && <div className="text-center text-muted-foreground text-xs py-2 bg-white rounded-md">
                    Nessun contatto
                  </div>}
              </div>
            </div>
            
            {/* Colonna Dati Fisici */}
            <div className="bg-white/80 p-3 rounded-lg shadow-sm hover:shadow transition-shadow duration-300">
              <h3 className="font-medium text-sm mb-2 flex items-center gap-1.5">
                <User size={14} className="text-primary" />
                Dati Fisici
              </h3>
              
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div className="flex flex-col items-center p-1.5 bg-white rounded hover:bg-gray-50 transition-colors">
                  <div className="flex items-center gap-1 text-muted-foreground text-xs mb-1">
                    <Ruler size={12} />
                    <span>Altezza</span>
                  </div>
                  <span className="font-medium">{client.height || "N/D"} {client.height ? "cm" : ""}</span>
                </div>
                
                <div className="flex flex-col items-center p-1.5 bg-white rounded hover:bg-gray-50 transition-colors">
                  <div className="flex items-center gap-1 text-muted-foreground text-xs mb-1">
                    <Weight size={12} />
                    <span>Peso</span>
                  </div>
                  <span className="font-medium">{client.weight || "N/D"} {client.weight ? "kg" : ""}</span>
                </div>
                
                <div className="flex flex-col items-center p-1.5 bg-white rounded hover:bg-gray-50 transition-colors">
                  <div className="flex items-center gap-1 text-muted-foreground text-xs mb-1">
                    <HeartPulse size={12} />
                    <span>BMI</span>
                  </div>
                  <span className="font-medium">{calculateBMI()}</span>
                </div>
              </div>
            </div>
            
            {/* Colonna Sport */}
            <div className="bg-white/80 p-3 rounded-lg shadow-sm hover:shadow transition-shadow duration-300">
              <h3 className="font-medium text-sm mb-2 flex items-center gap-1.5">
                <Award size={14} className="text-primary" />
                Sport
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {client.sports && client.sports.length > 0 ? client.sports.map((sport, index) => <Badge key={index} variant="secondary" className="px-2 py-1 text-xs bg-white hover:bg-gray-50 hover:scale-105 transition-all duration-300">
                    {sport}
                  </Badge>) : <span className="text-muted-foreground text-xs p-1">Nessuno sport</span>}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Contenuto principale con tab */}
      <Card className="shadow-sm border border-gray-100 overflow-hidden transition-all hover:shadow-md duration-300">
        <Tabs defaultValue="info" className="w-full">
          <div className="tabs-container relative border-b px-4">
            <TabsList className="mb-0 py-3 justify-start gap-1 bg-transparent">
              <TabsTrigger value="info" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary rounded-md transition-all">
                <span>Info</span>
              </TabsTrigger>
              <TabsTrigger value="packages" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary rounded-md transition-all">
                <span>Pacchetti</span>
              </TabsTrigger>
              <TabsTrigger value="courses" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary rounded-md transition-all">
                <span>Corsi</span>
              </TabsTrigger>
              <TabsTrigger value="workouts" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary rounded-md transition-all">
                <span>Allenamenti</span>
              </TabsTrigger>
              <TabsTrigger value="sessions" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary rounded-md transition-all">
                <span>Sessioni</span>
              </TabsTrigger>
              <TabsTrigger value="payments" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary rounded-md transition-all">
                <span>Pagamenti</span>
              </TabsTrigger>
            </TabsList>
            
            <div className="fade-left sm:hidden"></div>
            <div className="fade-right sm:hidden"></div>
          </div>
          
          <div className="p-4">
            <TabsContent value="info" className="animate-fade-in">
              <ClientInfoTab client={client} clientPackages={clientPackages} />
            </TabsContent>
            
            <TabsContent value="packages" className="animate-fade-in">
              <ClientPackagesTab clientPackages={clientPackages} onAddPackage={() => setIsPackageDialogOpen(true)} onDeletePackage={handleDeletePackage} />
            </TabsContent>

            <TabsContent value="courses" className="animate-fade-in">
              <ClientCoursesTab clientCourses={clientCourses} onAddCourse={() => setIsCourseDialogOpen(true)} onDeleteCourse={handleDeleteCourse} />
            </TabsContent>

            <TabsContent value="workouts" className="animate-fade-in">
              <ClientWorkoutsTab clientId={client.id} clientWorkouts={clientWorkouts} onAddWorkout={() => setIsWorkoutDialogOpen(true)} onDeleteWorkout={handleDeleteWorkout} onViewWorkout={handleViewWorkout} />
            </TabsContent>

            <TabsContent value="sessions" className="animate-fade-in">
              <ClientSessionsTab clientId={client.id} />
            </TabsContent>

            <TabsContent value="payments" className="animate-fade-in">
              <ClientPaymentsTab payments={payments} onAddPayment={() => setIsPaymentDialogOpen(true)} />
            </TabsContent>
          </div>
        </Tabs>
      </Card>
      
      {/* Dialog Modals */}
      <ClientFormDialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen} client={client} onSave={handleSaveClient} isSaving={isSaving} />
      
      <PackageDialog open={isPackageDialogOpen} onOpenChange={setIsPackageDialogOpen} availablePackages={availablePackages} selectedPackageId={selectedPackageId} onSelectPackage={setSelectedPackageId} onAssignPackage={handleAssignPackage} />
      
      <CourseDialog open={isCourseDialogOpen} onOpenChange={setIsCourseDialogOpen} availableCourses={availableCourses} selectedCourseId={selectedCourseId} onSelectCourse={setSelectedCourseId} onEnrollCourse={handleEnrollCourse} />
      
      <WorkoutDialog open={isWorkoutDialogOpen} onOpenChange={setIsWorkoutDialogOpen} availableWorkouts={availableWorkouts} selectedWorkoutId={selectedWorkoutId} onSelectWorkout={setSelectedWorkoutId} onAssignWorkout={handleAssignWorkout} />
      
      <PaymentDialog open={isPaymentDialogOpen} onOpenChange={setIsPaymentDialogOpen} newPayment={newPayment} onInputChange={handleInputChange} availablePackages={availablePackages} availableCourses={availableCourses} onAddPayment={handleAddPayment} />

      <WorkoutDetailsDialog isOpen={isWorkoutDetailsDialogOpen} onOpenChange={setIsWorkoutDetailsDialogOpen} workout={selectedWorkout} />
    </div>;
};
export default ClientDetail;